import axios, { AxiosInstance } from "axios";
import router from "@/router";
import { useSnackbar } from "@/shared/composables/useSnackbar";

let isRefreshing = false;
let failedQueue: {
  resolve: (token: string) => void;
  reject: (error: any) => void;
}[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

export function setupInterceptors(api: AxiosInstance) {
  // Request Interceptor
  api.interceptors.request.use(
    (config) => {
      // No adjuntar token en rutas públicas como login o refresh
      if (
        config.url &&
        (config.url.includes("/auth/login") ||
          config.url.includes("/auth/refresh"))
      ) {
        return config;
      }

      // Intentar obtener el token de donde esté guardado (ej. localStorage)
      const token = localStorage.getItem("access_token");

      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      // Manejar error de request
      return Promise.reject(error);
    }
  );

  // Response Interceptor
  api.interceptors.response.use(
    (response) => {
      // Si la respuesta es exitosa, solo devuélvela
      return response;
    },
    (error) => {
      const { showSnackbar } = useSnackbar();

      // Manejo de errores globales
      if (error.response) {
        const detail =
          error.response.data?.detail ||
          "Ocurrió un error inesperado al procesar la solicitud.";
        const title = error.response.data?.title || "Error de Operación";

        switch (error.response.status) {
          case 401: {
            const originalRequest = error.config;

            // If the failure is from login or refresh itself, don't loop
            if (
              originalRequest.url?.includes("/auth/login") ||
              originalRequest.url?.includes("/auth/refresh")
            ) {
              if (error.response.data && error.response.data.detail) {
                showSnackbar({ title: title, message: detail, type: "error" });
              }
              localStorage.removeItem("access_token");
              localStorage.removeItem("refresh_token");
              return Promise.reject(error);
            }

            const refreshToken = localStorage.getItem("refresh_token");
            if (!refreshToken) {
              if (error.response.data && error.response.data.detail) {
                showSnackbar({ title: title, message: detail, type: "error" });
              } else {
                showSnackbar({
                  title: "Sesión Expirada",
                  message:
                    "Tu sesión ha terminado. Por favor inicia sesión de nuevo.",
                  type: "warning",
                });
              }
              localStorage.removeItem("access_token");
              localStorage.removeItem("refresh_token");
              if (router.currentRoute.value.path !== "/login") {
                router.push("/login");
              }
              return Promise.reject(error);
            }

            if (!isRefreshing) {
              isRefreshing = true;

              const refreshAxios = axios.create({
                baseURL: api.defaults.baseURL,
                headers: { "Content-Type": "application/json" },
              });

              refreshAxios
                .post("/auth/refresh", { refreshToken })
                .then((res) => {
                  if (res.data && res.data.token) {
                    localStorage.setItem("access_token", res.data.token);
                    if (res.data.refreshToken) {
                      localStorage.setItem(
                        "refresh_token",
                        res.data.refreshToken
                      );
                    }
                    api.defaults.headers.common[
                      "Authorization"
                    ] = `Bearer ${res.data.token}`;
                    processQueue(null, res.data.token);
                  }
                })
                .catch((err) => {
                  processQueue(err, null);
                  localStorage.removeItem("access_token");
                  localStorage.removeItem("refresh_token");
                  showSnackbar({
                    title: "Sesión Expirada",
                    message:
                      "Tu sesión ha expirado por seguridad. Por favor, inicia sesión de nuevo.",
                    type: "warning",
                  });
                  if (router.currentRoute.value.path !== "/login") {
                    router.push("/login");
                  }
                })
                .finally(() => {
                  isRefreshing = false;
                });
            }

            return new Promise((resolve, reject) => {
              failedQueue.push({
                resolve: (token: string) => {
                  originalRequest.headers["Authorization"] = `Bearer ${token}`;
                  resolve(api(originalRequest));
                },
                reject: (err: any) => {
                  reject(err);
                },
              });
            });
          }
          case 403: // Forbidden
            showSnackbar({
              title: "Acceso Denegado",
              message: "No tienes permisos para realizar esta acción.",
              type: "error",
            });
            break;
          case 500: // Server error
            showSnackbar({
              title: "Falla del Servidor",
              message:
                "Ocurrió un error interno en el servidor. Intenta más tarde.",
              type: "error",
            });
            break;
          default:
            // Caso para 409 (Conflict), 400 (Bad Request), etc. usando los datos de tu backend
            showSnackbar({ title: title, message: detail, type: "error" });
        }
      } else if (error.request) {
        showSnackbar({
          title: "Error de Conexión",
          message:
            "El servidor no respondió. Verifica tu conexión a internet o intenta más tarde.",
          type: "error",
        });
      }

      return Promise.reject(error);
    }
  );
}
